import React, { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import SEOHelmet from "../components/SEOHelmet";
import Breadcrumbs from "../components/Breadcrumbs";
import JsonLd from "../components/JsonLd";
import { getBlogBySlug, getBlogImageForNumber } from "../constants/blogs";
import { getCategoryAssets } from "../utils/categoryAssets";
import { buildBreadcrumbListJsonLd } from "../utils/breadcrumbSchema";
import { absoluteUrl } from "../seo/siteUrl";
import "./BlogPostPage.css";

function buildBlogArticleJsonLd(post, slug) {
  const url = absoluteUrl(`/blogs/${slug}`);
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.h1,
    description: post.metaDescription,
    datePublished: post.datePublished,
    author: { "@type": "Organization", name: "Panthi Events" },
    publisher: {
      "@type": "Organization",
      name: "Panthi Events",
      logo: {
        "@type": "ImageObject",
        url: absoluteUrl("/favicon.png"),
      },
    },
    mainEntityOfPage: url,
    url,
  };
}

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getBlogBySlug(slug);

  const { gallery } = useMemo(() => getCategoryAssets("blogs"), []);

  const heroImage = useMemo(() => {
    if (!post) return null;
    return getBlogImageForNumber(post.number, gallery);
  }, [post, gallery]);

  if (!post) {
    return <Navigate to="/blogs" replace />;
  }

  const path = `/blogs/${slug}`;
  const globalKw =
    "event management company Ahmedabad, wedding planner Gujarat, corporate event organizer India, best event planner Gujarat";

  const breadcrumbLd = buildBreadcrumbListJsonLd([
    { name: "Home", path: "/" },
    { name: "Blogs", path: "/blogs" },
    { name: post.h1, path },
  ]);
  const articleLd = buildBlogArticleJsonLd(post, slug);

  return (
    <div className="blog-post-page">
      <SEOHelmet
        title={post.title}
        description={post.metaDescription}
        keywords={globalKw}
        canonicalPath={path}
        ogType="article"
      />
      <JsonLd data={breadcrumbLd} />
      <JsonLd data={articleLd} />

      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Blogs", to: "/blogs" },
          { label: post.h1 },
        ]}
      />

      <article className="blog-post-article">
        <div className="blog-post-container">
          {heroImage ? (
            <div className="blog-post-hero">
              <img
                src={heroImage.src}
                alt={heroImage.alt || post.h1}
                className="blog-post-hero-img"
                width={1280}
                height={720}
                loading="eager"
                decoding="async"
              />
            </div>
          ) : null}

          <header className="blog-post-header">
            <p className="blog-post-meta">
              <span className="blog-post-meta-category">{post.category}</span>
              <span className="blog-post-meta-sep">·</span>
              <time dateTime={post.datePublished}>{post.datePublished}</time>
              <span className="blog-post-meta-sep">·</span>
              <span>{post.readTime}</span>
            </p>
            <h1 className="blog-post-h1">{post.h1}</h1>
          </header>

          {post.sections.map((sec, i) => (
            <section key={i} className="blog-post-section">
              {sec.h2 && sec.h2 !== "Overview" ? (
                <h2 className="blog-post-h2">{sec.h2}</h2>
              ) : null}
              {sec.paragraphs.map((p, j) => {
                const isHtml = p.startsWith("<");
                if (isHtml) {
                  return (
                    <div
                      key={j}
                      className="blog-post-block-html"
                      dangerouslySetInnerHTML={{ __html: p }}
                    />
                  );
                }
                return (
                  <p key={j} className="blog-post-p">
                    {p}
                  </p>
                );
              })}
            </section>
          ))}

          <nav className="blog-post-internal" aria-label="Related links">
            <h2 className="blog-post-h2">Related pages</h2>
            <ul className="blog-post-internal-list">
              <li>
                <Link to="/wedding-events">Wedding events</Link>
              </li>
              <li>
                <Link to="/corporate-events">Corporate events</Link>
              </li>
              <li>
                <Link to="/event-management-ahmedabad">
                  Event management Ahmedabad
                </Link>
              </li>
              <li>
                <Link to="/contact">Get in touch</Link>
              </li>
            </ul>
          </nav>

          <div className="blog-post-back">
            <Link to="/blogs" className="blog-post-back-link">
              ← All blogs
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
